import React, { Component } from "react";
import equal from "fast-deep-equal";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { delay } from "@formant/core/common/delay";
import { AnalyticsChartType } from "@formant/core/model/AnalyticsChartType";
import { IAnalyticsModuleParameters } from "@formant/core/model/IAnalyticsModuleParameters";
import { ISqlQuery } from "@formant/core/model/ISqlQuery";
import { ISqlResult } from "@formant/core/model/ISqlResult";
import { Table } from "@formant/rectangle-sdk/src/widgets/Table";

import { analyticsStore } from "../../stores";
import { Scrollable } from "../common/Scrollable";
import { AnalyticsSummaryChart } from "./AnalyticsSummaryChart";
import { Chart } from "./Chart";
import { ErrorDisplay } from "./ErrorDisplay";
import { JsonViewer } from "./JsonViewer";
import { Loading } from "./Loading";
import { PieChart } from "./PieChart";
import { ChartBuilder } from "./utils";

interface IGenericRowChartProps {
    query: ISqlQuery;
    chartType?: AnalyticsChartType;
    params: IAnalyticsModuleParameters;
    start: string | undefined;
    end: string | undefined;
}

@observer
export class GenericRowChart extends Component<IGenericRowChartProps> {
    @observable
    private result?: ISqlResult | undefined = undefined;

    public async componentDidMount() {
        await this.getResult();
    }

    public async componentDidUpdate(prevProps: IGenericRowChartProps) {
        if (
            !equal(prevProps.params?.query, this.props.params?.query) ||
            this.props.start !== prevProps.start ||
            this.props.end !== prevProps.end
        ) {
            this.result = undefined;
            await this.getResult();
        } else if (
            !equal(
                prevProps.params.configuration?.advancedChartSettings,
                this.props.params.configuration?.advancedChartSettings,
            ) &&
            this.result
        ) {
            const tempResult = { ...this.result };
            this.result = undefined;
            await delay(100);
            this.result = tempResult;
        }
    }

    private async getResult() {
        this.result =
            (await analyticsStore.realListMatching({
                ...this.props.query,
            })) || undefined;
    }

    public render() {
        const { props, result } = this;
        const { chartType, query, start, end, params } = props;
        const advancedSettings = params?.configuration?.advancedChartSettings;
        const { aggregateLevel } = query;
        if (!result) {
            return <Loading columns={undefined} rows={undefined} />;
        }
        const { rows, columns, error } = result;

        if (error) {
            return <ErrorDisplay error={error} />;
        }

        if (!rows || !columns || rows.length === 0) {
            return <Loading columns={columns} rows={rows} />;
        }

        if (chartType === "json") {
            return <JsonViewer rows={rows} />;
        }

        let chartBuilder = new ChartBuilder(query, columns, rows);

        if (query.type === "advanced" && advancedSettings) {
            if (advancedSettings.xAxis) {
                chartBuilder = chartBuilder.setXAxis(advancedSettings.xAxis);
            }

            if (advancedSettings.partitionBy) {
                chartBuilder = chartBuilder.setPartitionBy(
                    advancedSettings.partitionBy,
                );
            }
        }

        chartBuilder = chartBuilder.partitionByDefault();

        if (chartType === "table" || chartType === undefined) {
            const rowData: {
                [key: string]: string | number | boolean;
            }[] = rows
                .slice(0, 200)
                .map(row =>
                    Object.fromEntries(
                        Object.entries(row).map(([key, value]) => [
                            key,
                            value === null ? "null" : value,
                        ]),
                    ),
                );

            return (
                <Scrollable>
                    <Table value={rowData} type="default" />
                </Scrollable>
            );
        }

        if (chartType === "pie") {
            return <PieChart data={chartBuilder.toPieSeries()} />;
        }

        if (chartType === "summary") {
            return <AnalyticsSummaryChart items={chartBuilder.toSummary()} />;
        }

        const xAxis = chartBuilder.options?.xAxis;

        if (!xAxis?.name) {
            return false;
        }

        return (
            <Chart
                chartType={chartType}
                series={chartBuilder.toSeries()}
                xAxisType="category"
                catagoryAxis={chartBuilder.getXAxis()}
                xAxisLabel={aggregateLevel || xAxis.name}
                start={start}
                end={end}
            />
        );
    }
}
